import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { testimonials, aggregateRating } from '@/mocks/testimonials';

const SITE_URL = import.meta.env.VITE_SITE_URL ?? 'https://greenlyelectronicsrecycling.com';

const StarRow = ({ count, filled }: { count: number; filled: number }) => (
  <div className="flex gap-0.5">
    {Array.from({ length: count }).map((_, i) => (
      <i
        key={i}
        className={`text-sm ${i < filled ? 'ri-star-fill text-yellow-400' : 'ri-star-line text-gray-300'}`}
      ></i>
    ))}
  </div>
);

const ratingBreakdown = [
  { stars: 5, pct: 91 },
  { stars: 4, pct: 6 },
  { stars: 3, pct: 2 },
  { stars: 2, pct: 1 },
  { stars: 1, pct: 0 },
];

const ReviewsJsonLd = () => (
  <script type="application/ld+json">{JSON.stringify({
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "WebPage",
        "@id": `${SITE_URL}/reviews#webpage`,
        "url": `${SITE_URL}/reviews`,
        "name": "Customer Reviews | Greenly Electronics Recycling Los Angeles",
        "description": "Read verified customer reviews for Greenly Electronics Recycling. Rated 4.9/5 from 500+ LA businesses and residents. Trusted for TV recycling, computer recycling, and data destruction.",
        "isPartOf": { "@id": `${SITE_URL}/#website` },
        "about": { "@id": `${SITE_URL}/#business` },
        "breadcrumb": {
          "@type": "BreadcrumbList",
          "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "Home", "item": `${SITE_URL}/` },
            { "@type": "ListItem", "position": 2, "name": "Reviews", "item": `${SITE_URL}/reviews` }
          ]
        }
      },
      {
        "@type": "LocalBusiness",
        "@id": `${SITE_URL}/reviews#business-reviews`,
        "name": "Greenly Electronics Recycling",
        "url": `${SITE_URL}`,
        "telephone": "+12136001000",
        "aggregateRating": {
          "@type": "AggregateRating",
          "ratingValue": aggregateRating.ratingValue,
          "reviewCount": aggregateRating.reviewCount,
          "bestRating": aggregateRating.bestRating,
          "worstRating": aggregateRating.worstRating
        },
        "review": testimonials.slice(0, 5).map((t) => ({
          "@type": "Review",
          "author": { "@type": "Person", "name": t.name },
          "reviewRating": {
            "@type": "Rating",
            "ratingValue": t.rating,
            "bestRating": 5,
            "worstRating": 1
          },
          "reviewBody": t.text,
          "datePublished": "2026-01-01"
        }))
      }
    ]
  })}</script>
);

const ReviewsPage = () => {
  const [expanded, setExpanded] = useState<number | null>(null);

  return (
    <>
      <title>Customer Reviews | Greenly Electronics Recycling Los Angeles</title>
      <meta name="description" content="Rated 4.9/5 by 500+ LA businesses & residents. Read verified reviews for Greenly Electronics Recycling — trusted for TV recycling, computer recycling & data destruction in Los Angeles." />
      <meta name="keywords" content="Greenly Electronics Recycling reviews, electronics recycling Los Angeles reviews, e-waste recycling reviews LA, customer testimonials Greenly" />
      <meta name="robots" content="index, follow" />
      <meta name="last-modified" content="2026-05-11" />
      <link rel="canonical" href={`${SITE_URL}/reviews`} />
      <ReviewsJsonLd />
      <Navbar />
      <main>
        {/* Hero */}
        <section className="pt-24 pb-16 md:pt-32 md:pb-20 bg-gradient-to-br from-[#0F3D22] via-[#1B6B3A] to-[#2D8653]">
          <div className="max-w-7xl mx-auto px-4 md:px-6 text-center">
            <Breadcrumb items={[{ label: 'Home', path: '/' }, { label: 'Reviews' }]} />
            <div className="inline-flex items-center gap-2 text-[#4CAF82] text-xs font-bold uppercase tracking-widest mb-4">
              <i className="ri-star-fill text-yellow-400"></i>
              Customer Reviews
            </div>
            <h1 className="text-3xl md:text-5xl font-extrabold text-white leading-tight mb-4">
              Trusted by Hundreds of<br className="hidden md:block" /> LA Businesses
            </h1>
            <p className="text-green-100 text-base md:text-lg max-w-2xl mx-auto leading-relaxed">
              From hospitals to schools, startups to corporations — Los Angeles businesses rely on Greenly for certified, responsible electronics recycling.
            </p>
          </div>
        </section>

        {/* Rating Summary */}
        <section className="py-12 md:py-16 bg-white border-b border-gray-100">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <div className="flex flex-col md:flex-row items-center justify-between gap-8">
              {/* Aggregate Score */}
              <div className="text-center md:text-left">
                <div className="flex items-center justify-center md:justify-start gap-4 mb-2">
                  <div className="text-6xl font-extrabold text-gray-900 leading-none">
                    {aggregateRating.ratingValue}
                  </div>
                  <div className="text-left">
                    <StarRow count={5} filled={5} />
                    <div className="text-gray-500 text-sm mt-1">
                      Based on <strong className="text-gray-900">{aggregateRating.reviewCount.toLocaleString()}</strong> reviews
                    </div>
                  </div>
                </div>
                <div className="flex items-center justify-center md:justify-start gap-2 text-gray-500 text-sm">
                  <i className="ri-google-fill text-lg" style={{ color: '#4285F4' }}></i>
                  <span>Verified Google Reviews</span>
                </div>
              </div>

              {/* Rating Breakdown */}
              <div className="w-full md:w-80 space-y-2">
                {ratingBreakdown.map((r) => (
                  <div key={r.stars} className="flex items-center gap-3">
                    <span className="text-sm text-gray-600 w-4 shrink-0 font-medium">{r.stars}</span>
                    <i className="ri-star-fill text-yellow-400 text-sm shrink-0"></i>
                    <div className="flex-1 h-2.5 bg-gray-100 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-yellow-400 rounded-full transition-all duration-500"
                        style={{ width: `${r.pct}%` }}
                      ></div>
                    </div>
                    <span className="text-sm text-gray-400 w-10 text-right shrink-0">{r.pct}%</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Reviews Grid */}
        <section className="py-16 md:py-20 bg-[#F4FAF7]">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {testimonials.map((t) => {
                const isLong = t.text.length > 160;
                const isExpanded = expanded === t.id;
                const displayText = isLong && !isExpanded ? t.text.slice(0, 160) + '…' : t.text;

                return (
                  <article
                    key={t.id}
                    className="bg-white rounded-2xl p-6 flex flex-col border border-gray-100 hover:border-[#1B6B3A]/20 hover:shadow-lg transition-all"
                  >
                    {/* Top row: stars + source */}
                    <div className="flex items-center justify-between mb-4">
                      <StarRow count={5} filled={t.rating} />
                      <div className="flex items-center gap-1.5 text-gray-300 text-xs">
                        <i className="ri-google-fill text-base" style={{ color: '#4285F4' }}></i>
                        <span className="text-gray-400">Google</span>
                      </div>
                    </div>

                    {/* Quote */}
                    <blockquote className="text-gray-600 text-sm leading-relaxed flex-1 mb-1">
                      &ldquo;{displayText}&rdquo;
                    </blockquote>
                    {isLong && (
                      <button
                        onClick={() => setExpanded(isExpanded ? null : t.id)}
                        className="text-[#1B6B3A] text-xs font-semibold hover:underline cursor-pointer text-left mb-3 mt-1"
                      >
                        {isExpanded ? 'Show less' : 'Read more'}
                      </button>
                    )}

                    {/* Divider */}
                    <div className="border-t border-gray-100 mt-4 pt-4 flex items-center gap-3">
                      <img
                        src={t.avatar}
                        alt={t.name}
                        className="w-10 h-10 rounded-full object-cover object-top shrink-0"
                        loading="lazy"
                        width={40}
                        height={40}
                      />
                      <div className="min-w-0">
                        <div className="font-bold text-gray-900 text-sm truncate">{t.name}</div>
                        <div className="text-gray-400 text-xs truncate">{t.title}, {t.company}</div>
                        <div className="flex items-center gap-1 text-gray-300 text-xs mt-0.5">
                          <i className="ri-map-pin-line text-[#1B6B3A] text-xs"></i>
                          <span className="text-gray-400">{t.location}</span>
                        </div>
                      </div>
                    </div>
                  </article>
                );
              })}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 md:py-20 bg-white">
          <div className="max-w-3xl mx-auto px-4 md:px-6 text-center">
            <h2 className="text-2xl md:text-3xl font-extrabold text-gray-900 mb-4">
              Join Hundreds of Satisfied LA Businesses
            </h2>
            <p className="text-gray-500 text-base mb-8 leading-relaxed">
              Get certified, responsible e-waste recycling with free pickup for LA businesses. Experience the Greenly difference today.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <a
                href="tel:+12136001000"
                className="inline-flex items-center gap-2 bg-[#1B6B3A] hover:bg-[#2D8653] text-white font-bold px-7 py-3 rounded-full transition-colors cursor-pointer whitespace-nowrap text-sm"
              >
                <i className="ri-phone-fill"></i>
                Call (213) 600-1000
              </a>
              <a
                href="/pickup-request"
                className="inline-flex items-center gap-2 border-2 border-[#1B6B3A] text-[#1B6B3A] hover:bg-[#1B6B3A] hover:text-white font-bold px-7 py-3 rounded-full transition-colors cursor-pointer whitespace-nowrap text-sm"
              >
                <i className="ri-calendar-check-line"></i>
                Schedule Free Pickup
              </a>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
};

export default ReviewsPage;